import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Table, Button, Space, Tag, message, Select, Input, Collapse } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import CreateManagerModal from '../CreateManagerModal';
import EditManagerModal from '../EditManagerModal';
import InviteManagerModal from '../InviteManagerModal';
import { ManagerService } from '@/services/manager.service';
import apiService from '@/services/api.service';
import type { Manager, Club } from '@/types/models';
import type { ApiResponse } from '@/types/api';
import { logger } from '@/utils/logger';
import { API_ENDPOINTS } from '@/constants/api.constants';

const ManagersPage: React.FC = () => {
    const location = useLocation();
    const [managers, setManagers] = useState<Manager[]>([]);
    const [filteredManagers, setFilteredManagers] = useState<Manager[]>([]);
    const [clubs, setClubs] = useState<Club[]>([]);
    const [isManagersLoading, setIsManagersLoading] = useState(false);
    const [managerStatusFilter, setManagerStatusFilter] = useState<string>('all');
    const [managerClubFilter, setManagerClubFilter] = useState<number | null>(null);
    const [managerFederationFilter, setManagerFederationFilter] = useState<number | null>(null);
    const [federations, setFederations] = useState<any[]>([]);
    const [isCreateManagerModalVisible, setIsCreateManagerModalVisible] = useState(false);
    const [isEditManagerModalVisible, setIsEditManagerModalVisible] = useState(false);
    const [isInviteManagerModalVisible, setIsInviteManagerModalVisible] = useState(false);
    const [selectedManager, setSelectedManager] = useState<Manager | null>(null);
    const [invitations, setInvitations] = useState<any[]>([]);
    const [invitationsLoading, setInvitationsLoading] = useState(false);

    const fetchManagers = async () => {
        try {
            setIsManagersLoading(true);
            const response = await ManagerService.getAllManagers();
            const managersData = response as Manager[];
            setManagers(managersData);
            filterManagers(managersData, managerStatusFilter, managerClubFilter, managerFederationFilter);
        } catch (error) {
            logger.error('Error fetching managers:', error);
            message.error('Ошибка при загрузке списка менеджеров');
        } finally {
            setIsManagersLoading(false);
        }
    };

    const fetchClubs = async () => {
        try {
            const response = await apiService.get<ApiResponse<Club[]>>(API_ENDPOINTS.CLUBS.BASE);
            const clubsData = response.data?.data;
            if (Array.isArray(clubsData)) {
                setClubs(clubsData);
            } else if (clubsData && typeof clubsData === 'object' && 'rows' in clubsData) {
                setClubs((clubsData as any).rows || []);
            } else {
                setClubs([]);
            }
        } catch (error) {
            logger.error('Error fetching clubs:', error);
        }
    };

    const filterManagers = (managersList: Manager[], status: string, clubId: number | null, federationId: number | null) => {
        let filtered = [...managersList];
        if (status !== 'all') {
            filtered = filtered.filter(m => m.status === status);
        }
        if (clubId !== null) {
            filtered = filtered.filter(m => m.clubId === clubId);
        }
        if (federationId !== null) {
            if (federationId === -1) {
                // Фильтр "Без федерации"
                filtered = filtered.filter(m => !(m.club as any)?.federation?.id);
            } else {
                filtered = filtered.filter(m => (m.club as any)?.federation?.id === federationId);
            }
        }
        setFilteredManagers(filtered);
    };

    const fetchFederations = async () => {
        try {
            const response = await apiService.get<ApiResponse<any[] | { items: any[] }>>(API_ENDPOINTS.FEDERATIONS.BASE, { params: { limit: 1000 } });
            const fedsData = response.data?.data;
            let fedsArray: any[] = [];
            if (Array.isArray(fedsData)) {
                fedsArray = fedsData;
            } else if (fedsData && typeof fedsData === 'object' && 'items' in fedsData) {
                fedsArray = (fedsData as any).items || [];
            }
            setFederations(fedsArray);
        } catch (error) {
            logger.error('Error fetching federations:', error);
        }
    };

    useEffect(() => {
        fetchClubs();
        fetchFederations();
    }, []);

    useEffect(() => {
        if (location.pathname === '/admin/managers') {
            fetchManagers();
        }
    }, [location.pathname]);

    useEffect(() => {
        filterManagers(managers, managerStatusFilter, managerClubFilter, managerFederationFilter);
    }, [managers, managerStatusFilter, managerClubFilter, managerFederationFilter]);

    const fetchInvitations = async (clubId: number) => {
        try {
            setInvitationsLoading(true);
            const res = await apiService.get(API_ENDPOINTS.CLUB_MANAGERS.INVITATIONS_BY_CLUB(clubId));
            setInvitations((res as any)?.data?.data ?? []);
        } catch (e) {
            logger.error('Error fetching invitations:', e);
            setInvitations([]);
        } finally {
            setInvitationsLoading(false);
        }
    };

    const handleCancelInvitation = async (id: number, clubId: number) => {
        try {
            await apiService.delete(API_ENDPOINTS.CLUB_MANAGERS.CANCEL_INVITATION(id, clubId));
            message.success('Приглашение отменено');
            if (managerClubFilter) fetchInvitations(managerClubFilter);
        } catch (e: any) {
            message.error(e.response?.data?.message || 'Ошибка при отмене');
        }
    };

    const managerColumns: ColumnsType<Manager> = [
        {
            title: 'ФИО',
            key: 'fullName',
            render: (_: unknown, record: Manager) => 
                `${record.lastName} ${record.firstName}${record.middleName ? ` ${record.middleName}` : ''}`,
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                        placeholder="Поиск по ФИО"
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                    />
                    <Space>
                        <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
                            Поиск
                        </Button>
                        <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
                            Сброс
                        </Button>
                    </Space>
                </div>
            ),
            onFilter: (value, record) => {
                const fullName = `${record.lastName} ${record.firstName}${record.middleName ? ` ${record.middleName}` : ''}`;
                return fullName.toLowerCase().includes(String(value).toLowerCase());
            },
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                        placeholder="Поиск по email"
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                    />
                    <Space>
                        <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
                            Поиск
                        </Button>
                        <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
                            Сброс
                        </Button>
                    </Space>
                </div>
            ),
            onFilter: (value, record) =>
                record.email?.toLowerCase().includes(String(value).toLowerCase()),
        },
        {
            title: 'Клуб',
            key: 'club',
            render: (_: unknown, record: Manager) => record.club?.name || 'Не указан',
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            key: 'status',
            filters: [
                { text: 'Активен', value: 'active' },
                { text: 'Неактивен', value: 'inactive' },
            ],
            onFilter: (value, record) => record.status === value,
            render: (status: string) => (
                <Tag color={status === 'active' ? 'green' : 'red'}>
                    {status === 'active' ? 'Активен' : 'Неактивен'}
                </Tag>
            ),
        },
        {
            title: 'Действия',
            key: 'actions',
            render: (_: unknown, record: Manager) => (
                <Space>
                    <Button type="link" onClick={() => {
                        setSelectedManager(record);
                        setIsEditManagerModalVisible(true);
                    }}>
                        Редактировать
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                <Space>
                    <Button
                        type="primary"
                        onClick={() => {
                            setSelectedManager(null);
                            setIsCreateManagerModalVisible(true);
                        }}
                    >
                        Добавить менеджера
                    </Button>
                    <Button
                        onClick={() => setIsInviteManagerModalVisible(true)}
                    >
                        Пригласить по email
                    </Button>
                </Space>
                <div style={{ display: 'flex', gap: '16px' }}>
                    <div>
                        <span style={{ marginRight: 8 }}>Статус:</span>
                        <Select
                            value={managerStatusFilter}
                            style={{ width: 120 }}
                            onChange={(value) => {
                                setManagerStatusFilter(value);
                                filterManagers(managers, value, managerClubFilter, managerFederationFilter);
                            }}
                            options={[
                                { value: 'all', label: 'Все' },
                                { value: 'active', label: 'Активные' },
                                { value: 'inactive', label: 'Неактивные' },
                                { value: 'pending', label: 'Ожидающие' }
                            ]}
                        />
                    </div>
                    <div>
                        <span style={{ marginRight: 8 }}>Клуб:</span>
                        <Select
                            value={managerClubFilter}
                            style={{ width: 200 }}
                            onChange={(value: string | number | null) => {
                                const clubId = (value === 'all' || value === null) ? null : (typeof value === 'number' ? value : Number(value));
                                setManagerClubFilter(clubId);
                                filterManagers(managers, managerStatusFilter, clubId, managerFederationFilter);
                                if (clubId) fetchInvitations(clubId);
                                else setInvitations([]);
                            }}
                            allowClear
                            options={[
                                { value: 'all', label: 'Все клубы' },
                                ...clubs.map(club => ({
                                    value: club.id,
                                    label: club.name
                                }))
                            ]}
                        />
                    </div>
                    <div>
                        <span style={{ marginRight: 8 }}>Федерация:</span>
                        <Select
                            value={managerFederationFilter}
                            style={{ width: 200 }}
                            onChange={(value: string | number | null) => {
                                const federationId = (value === 'all' || value === null) ? null : (typeof value === 'number' ? value : Number(value));
                                setManagerFederationFilter(federationId);
                                filterManagers(managers, managerStatusFilter, managerClubFilter, federationId);
                            }}
                            allowClear
                            placeholder="Все федерации"
                            options={[
                                { value: 'all', label: 'Все федерации' },
                                { value: -1, label: 'Без федерации' },
                                ...federations.map(federation => ({
                                    value: federation.id,
                                    label: federation.name
                                }))
                            ]}
                        />
                    </div>
                </div>
            </div>
            {managerClubFilter && (
                <Collapse style={{ marginBottom: 16 }}>
                    <Collapse.Panel
                        header={`Ожидающие приглашения (${invitations.filter((i: any) => i.status === 'pending').length})`}
                        key="invitations"
                    >
                        {invitationsLoading ? (
                            <div style={{ padding: 16, textAlign: 'center' }}>Загрузка...</div>
                        ) : invitations.length === 0 ? (
                            <div style={{ padding: 16, color: '#999' }}>Нет приглашений</div>
                        ) : (
                            <Table
                                size="small"
                                dataSource={invitations}
                                rowKey="id"
                                columns={[
                                    { title: 'Email', dataIndex: 'email', key: 'email' },
                                    { title: 'Роль', dataIndex: 'role', key: 'role', render: (r: string) => r === 'head_manager' ? 'Главный менеджер' : 'Менеджер' },
                                    { title: 'Статус', dataIndex: 'status', key: 'status', render: (s: string) => <Tag color={s === 'pending' ? 'blue' : s === 'accepted' ? 'green' : 'default'}>{s === 'pending' ? 'Ожидает' : s === 'accepted' ? 'Принято' : s}</Tag> },
                                    { title: 'Срок', dataIndex: 'expiresAt', key: 'expiresAt', render: (d: string) => d ? new Date(d).toLocaleString('ru-RU') : '-' },
                                    {
                                        title: 'Действия',
                                        key: 'actions',
                                        render: (_: unknown, record: any) =>
                                            record.status === 'pending' ? (
                                                <Button type="link" danger size="small" onClick={() => handleCancelInvitation(record.id, record.clubId)}>
                                                    Отменить
                                                </Button>
                                            ) : null,
                                    },
                                ]}
                                pagination={false}
                            />
                        )}
                    </Collapse.Panel>
                </Collapse>
            )}
            <Table
                columns={managerColumns}
                dataSource={filteredManagers}
                rowKey="id"
                loading={isManagersLoading}
            />
            <InviteManagerModal
                visible={isInviteManagerModalVisible}
                clubs={clubs}
                onCancel={() => setIsInviteManagerModalVisible(false)}
                onSuccess={() => {
                    setIsInviteManagerModalVisible(false);
                    fetchManagers();
                    if (managerClubFilter) fetchInvitations(managerClubFilter);
                }}
            />
            <CreateManagerModal
                visible={isCreateManagerModalVisible}
                onCancel={() => setIsCreateManagerModalVisible(false)}
                onSuccess={() => {
                    setIsCreateManagerModalVisible(false);
                    fetchManagers();
                }}
            />
            <EditManagerModal
                visible={isEditManagerModalVisible}
                manager={selectedManager}
                onCancel={() => {
                    setIsEditManagerModalVisible(false);
                    setSelectedManager(null);
                }}
                onSuccess={() => {
                    setIsEditManagerModalVisible(false);
                    setSelectedManager(null);
                    fetchManagers();
                }}
            />
        </div>
    );
};

export default ManagersPage;


